import React from 'react';
import { ViewMode } from '../hooks/usePlantafel';
import styles from './PlantafelBoard.module.css';

const LABEL_WIDTH = 120;

interface PlantafelGridHeadersProps {
  viewMode: ViewMode;
  days: any[];
  colWidth: number;
  settings: any;
  hourMarkers: number[];
  absences: any[];
  absenceCountsByDate: Record<string, { approved: number; requested: number }>;
  activeEmployeeId: number | null;
  setAbsModal: (state: { open: boolean; date?: string; type?: 'approved' | 'requested'; anchor?: { top: number; left: number } }) => void;
}

export function PlantafelGridHeaders({
  viewMode,
  days,
  colWidth,
  settings,
  hourMarkers,
  absences,
  absenceCountsByDate,
  activeEmployeeId,
  setAbsModal,
}: PlantafelGridHeadersProps): React.JSX.Element {
  const COL_WIDTH = colWidth;

  return (
    <div className={styles.stickyHeader}>
      {viewMode === 'year' && (
        <div className={styles.yearMonthRow}>
          <div className={styles.yearMonthLabelCell} style={{ width: LABEL_WIDTH }}>
            Monat
          </div>
          {(() => {
            const monthGroups: { month: number; count: number }[] = [];
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
            let currentMonth = -1;
            days.forEach((day: any) => {
              if (day.month !== currentMonth) {
                monthGroups.push({ month: day.month, count: 1 });
                currentMonth = day.month;
              } else {
                monthGroups[monthGroups.length - 1].count++;
              }
            });
            return monthGroups.map((group, idx) => (
              <div
                key={idx}
                className={styles.yearMonthGroupCell}
                style={{ width: group.count * COL_WIDTH }}
              >
                {monthNames[group.month]}
              </div>
            ));
          })()}
        </div>
      )}

      <div className={styles.dayColumnsRow}>
        <div
          className={styles.teamLabelCell}
          style={{ width: LABEL_WIDTH, padding: viewMode === 'week' ? '6px 8px' : '4px 8px' }}
        >
          Team
        </div>
        {days.map((day: any) => {
          if (viewMode === 'month') {
            const isWeekend = day.dayName === 'Sa' || day.dayName === 'So';
            return (
              <div
                key={day.dateStr}
                className={styles.dayHeaderCellMonth}
                style={{
                  width: COL_WIDTH,
                  flexShrink: 0,
                  color: isWeekend ? '#9ca3af' : '#03045e',
                  background: isWeekend ? '#f9fafb' : 'white',
                  borderRight: '1px solid #e0e3ef',
                  fontWeight: 600,
                  textAlign: 'center',
                }}
                title={`${day.dayName}, ${day.label}.`}
              >
                <div>{day.dayName}</div>
                <div style={{ fontSize: '11px' }}>{day.label}</div>
              </div>
            );
          }

          if (viewMode === 'year') {
            return (
              <div
                key={day.dateStr}
                className={styles.dayHeaderCellYear}
                style={{
                  width: COL_WIDTH,
                  flexShrink: 0,
                  borderRight: '1px solid #e0e3ef',
                  background: 'white',
                  textAlign: 'center',
                }}
                title={`KW ${day.weekNum}`}
              >
                {day.label}
              </div>
            );
          }

          const absenceCounts = absenceCountsByDate[day.dateStr] || { approved: 0, requested: 0 };
          const activeEmpAbsence = activeEmployeeId ? absences.find((a: any) => {
            if (a.employee !== activeEmployeeId) return false;
            const dayStart = new Date(`${day.dateStr}T00:00:00`).getTime();
            const dayEnd = new Date(`${day.dateStr}T23:59:59`).getTime();
            const absStart = new Date(a.start_datetime).getTime();
            const absEnd = new Date(a.end_datetime).getTime();
            return absStart <= dayEnd && absEnd >= dayStart;
          }) : null;

          const hasActiveEmpApproved = activeEmpAbsence?.status === 'APPROVED';
          const hasActiveEmpRequested = activeEmpAbsence?.status === 'REQUESTED';
          const hasAbsences = absenceCounts.approved > 0 || absenceCounts.requested > 0 || hasActiveEmpApproved || hasActiveEmpRequested;

          return (
            <div
              key={day.dateStr}
              className={styles.dayHeaderCellWeek}
              style={{
                width: COL_WIDTH,
                flexShrink: 0,
                borderRight: '1px solid #e0e3ef',
                background: 'white',
                textAlign: 'center',
              }}
            >
              <div className={styles.dayLabelRow}>
                <span>{day.label}</span>
                {hasAbsences && (
                  <span className="pills">
                    {(absenceCounts.approved > 0 || hasActiveEmpApproved) && (
                      <span
                        className="pill pill--red"
                        role="button"
                        onClick={(e) => {
                          const el = e.currentTarget as HTMLElement;
                          const rect = el.getBoundingClientRect();
                          const modalWidth = 320;
                          const margin = 8;
                          let left = rect.left + window.scrollX;
                          let top = rect.bottom + window.scrollY + 6;
                          const maxLeft = window.scrollX + window.innerWidth - modalWidth - margin;
                          if (left > maxLeft) left = Math.max(window.scrollX + margin, maxLeft);
                          setAbsModal({ open: true, date: day.dateStr, type: 'approved', anchor: { top, left } });
                        }}
                        style={{
                          cursor: 'pointer',
                          ...(hasActiveEmpApproved ? {
                            boxShadow: '0 0 8px 2px rgba(220, 53, 69, 0.6)',
                            animation: 'pulse-glow 3.5s ease-in-out forwards',
                          } : {}),
                        }}
                        title={`${absenceCounts.approved} genehmigte Abwesenheit${absenceCounts.approved !== 1 ? 'en' : ''} - Klick für Details`}
                      >
                        {absenceCounts.approved > 0 ? absenceCounts.approved : '!'}
                      </span>
                    )}
                    {(absenceCounts.requested > 0 || (hasActiveEmpRequested && !hasActiveEmpApproved)) && (
                      <span
                        className="pill pill--orange"
                        role="button"
                        onClick={(e) => {
                          const el = e.currentTarget as HTMLElement;
                          const rect = el.getBoundingClientRect();
                          const modalWidth = 320;
                          const margin = 8;
                          let left = rect.left + window.scrollX;
                          let top = rect.bottom + window.scrollY + 6;
                          const maxLeft = window.scrollX + window.innerWidth - modalWidth - margin;
                          if (left > maxLeft) left = Math.max(window.scrollX + margin, maxLeft);
                          setAbsModal({ open: true, date: day.dateStr, type: 'requested', anchor: { top, left } });
                        }}
                        style={{
                          cursor: 'pointer',
                          ...(hasActiveEmpRequested && !hasActiveEmpApproved ? {
                            boxShadow: '0 0 6px 1px rgba(245, 158, 11, 0.5)',
                          } : {}),
                        }}
                        title={`${absenceCounts.requested} angeforderte Abwesenheit${absenceCounts.requested !== 1 ? 'en' : ''} - Klick für Details`}
                      >
                        {absenceCounts.requested > 0 ? absenceCounts.requested : '?'}
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {viewMode === 'week' && (
        <div className={styles.timeRulerRow}>
          <div className={styles.timeRulerLabelCell} style={{ width: LABEL_WIDTH }}>
            {settings.day_start} - {settings.day_end}
          </div>
          {days.map((day: any, dayIdx: number) => {
            const isEvenDay = dayIdx % 2 === 0;
            const workWindowMinutes = settings.day_end_minutes - settings.day_start_minutes;
            const startHour = Math.floor(settings.day_start_minutes / 60);
            const rulerBg = isEvenDay ? '#f3f4f6' : '#fafbfc';
            return (
              <div
                key={`ruler-${day.dateStr}`}
                className={styles.timeRulerDayCell}
                style={{ width: COL_WIDTH, background: rulerBg }}
              >
                {hourMarkers.map((hour) => {
                  const isEvenHour = hour % 2 === 0;
                  const hourMinutes = hour * 60;
                  const minutesFromStart = hourMinutes - settings.day_start_minutes;
                  const linePosition = (minutesFromStart / workWindowMinutes) * 100;
                  if (linePosition < 0 || linePosition > 100) return null;
                  return (
                    <React.Fragment key={hour}>
                      {hour !== startHour && (
                        <div
                          style={{
                            position: 'absolute',
                            left: `${linePosition}%`,
                            top: 0,
                            bottom: 0,
                            width: '1px',
                            background: isEvenHour ? '#ccc' : '#e5e5e5',
                            transform: 'translateX(-50%)',
                          }}
                        />
                      )}
                      {isEvenHour && (
                        <span
                          style={{
                            position: 'absolute',
                            left: `${linePosition}%`,
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            fontSize: '9px',
                            color: '#666',
                            fontWeight: 600,
                            background: rulerBg,
                            padding: '0 2px',
                            zIndex: 1,
                          }}
                        >
                          {hour.toString().padStart(2, '0')}
                        </span>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
